import { type Platforms } from 'shared/api/accounts'
import { OAUTH_ADS_FLOW_DATASOURCES } from 'shared/copilot/flowDataAgentConstants'
import { type AlvieDatasourceOption } from 'shared/copilot/utils/alvieDatasourceOptions'

const DATASOURCE_SOURCE_TYPES = [
  { sourceType: 'ga4_property', platform: 'ga4', label: 'Google Analytics 4' },
  { sourceType: 'gsc_property', platform: 'google_search_console', label: 'Google Search Console' },
  { sourceType: 'google_ads_account', platform: 'google', label: 'Google Ads' },
  ...OAUTH_ADS_FLOW_DATASOURCES.map((entry) => ({
    sourceType: entry.entityType,
    platform: entry.alviePlatform,
    label: entry.sourceLabel
  })),
  { sourceType: 'reporting_solution', platform: null, label: 'Alvie Reporting' }
] as const satisfies ReadonlyArray<{ sourceType: string; platform: Platforms | null; label: string }>

/** Flow `suggest_data_source` `source_type` values. */
export type SuggestDataSourceType = (typeof DATASOURCE_SOURCE_TYPES)[number]['sourceType']

export interface SuggestDataSourceArgs {
  sourceType: string
  reasoning: string
  suggestedPrompt: string
}

const SOURCE_TYPE_TO_PLATFORM = Object.fromEntries(
  DATASOURCE_SOURCE_TYPES.filter((entry) => entry.platform != null).map((entry) => [entry.sourceType, entry.platform])
) as Partial<Record<SuggestDataSourceType, Platforms>>

const SOURCE_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  DATASOURCE_SOURCE_TYPES.map((entry) => [entry.sourceType, entry.label])
)

/**
 * Parses `suggest_data_source` tool-call arguments for the suggestion card.
 */
export const parseSuggestDataSourceArgs = (args: Record<string, unknown>): SuggestDataSourceArgs => {
  const sourceType = typeof args.source_type === 'string' ? args.source_type : ''
  const reasoning = typeof args.reasoning === 'string' ? args.reasoning : ''
  const suggestedPrompt = typeof args.suggested_prompt === 'string' ? args.suggested_prompt : ''

  return {
    sourceType,
    reasoning,
    suggestedPrompt
  }
}

/**
 * Returns a human-readable label for a Flow `source_type`.
 */
export const getSuggestDataSourceTypeLabel = (sourceType: string): string =>
  SOURCE_TYPE_LABELS[sourceType] ?? sourceType

/**
 * Maps a Flow `source_type` to an Alvie account platform when one exists.
 */
export const getPlatformForSuggestDataSourceType = (sourceType: string): Platforms | null => {
  return SOURCE_TYPE_TO_PLATFORM[sourceType as SuggestDataSourceType] ?? null
}

/**
 * Filters datasource options to those matching the suggested source type.
 *
 * Only returns options the data agent can actually answer on: a supported
 * platform, a connected account, and complete platform metadata. The last one
 * matters because the card can auto-select what it suggests — suggesting an
 * account the picker itself disables would start an analysis the agent refuses.
 */
export const filterOptionsForSuggestDataSource = (
  options: AlvieDatasourceOption[],
  sourceType: string
): AlvieDatasourceOption[] => {
  const platform = getPlatformForSuggestDataSourceType(sourceType)
  if (platform == null) {
    return []
  }

  return options.filter(
    (option) => option.platform === platform && option.supported && option.connected && !option.needsSetup
  )
}
